// Common Mistakes type for exercises
export interface CommonMistake {
  description: string;
  images: string[];
}

// ─── Warmup types ────────────────────────────────────────────────────────────

export type WarmupTriggerType = 'muscle_group' | 'body_region' | 'movement_pattern' | 'universal';

export interface WarmupItem {
  name: string;
  description: string;
  orderIndex: number;
  reps?: number;
  durationSeconds?: number;
}

export interface WarmupTemplate {
  id: number;
  name: string;
  triggerType: WarmupTriggerType;
  triggerValue: string | null;
  durationEstimateMinutes: number;
  items: WarmupItem[];
  isSystem: boolean;
  createdAt: string;
  updatedAt: string | null;
}

export interface WarmupPlan {
  items: WarmupItem[];
  durationEstimateMinutes: number;
  templateCount: number;
}

export interface CreateWarmupTemplateInput {
  name: string;
  triggerType: WarmupTriggerType;
  triggerValue: string | null;
  durationEstimateMinutes: number;
  items: WarmupItem[];
}

export type TrainingLabel = 'base' | 'build' | 'intensity' | 'deload' | 'test';
export const WORKOUT_SESSION_NOTES_MAX_LENGTH = 2000;

export const TRAINING_LABEL_OPTIONS: Array<{ value: TrainingLabel; label: string }> = [
  { value: 'base', label: 'Базова' },
  { value: 'build', label: 'Натрупване' },
  { value: 'intensity', label: 'Интензивност' },
  { value: 'deload', label: 'Делоуд' },
  { value: 'test', label: 'Тест' },
];

// Backend API types that match the Symfony entities
export interface Gym {
  id: number;
  name: string;
  type?: string;
  address?: string;
  equipment?: Array<Equipment | string>;
  createdAt: string;
}

export interface Equipment {
  id: number;
  name: string;
  type: string;
  description?: string;
  image?: string;
  make?: string | null;
  initialResistance?: string | number | null; // decimal returned as string from API
  muscleGroups?: string[] | null;
  gyms?: Array<Gym | string> | null;
  gym?: Gym | string | null; // Can be Gym object or IRI string
  createdAt: string;
}

export interface Exercise {
  id: number;
  name: string;
  description?: string;
  category: string;
  stimulusToFatigueRatio: string;
  equipment: string; // Derived from equipmentEntity name on the backend
  equipmentEntity?: Equipment | string | null; // Equipment entity object or IRI string
  muscleGroup: string;
  primaryMuscleGroup?: string;
  secondaryMuscleGroup?: string;
  tertiaryMuscleGroup?: string;
  instructions?: string;
  difficulty: string;
  movementPattern?: string;
  bodyRegion?: string;
  anatomyImages?: string[] | null;
  instructionImages?: string[] | null;
  youtubeVideoUrl?: string | null;
  cautionNotes?: string | null;
  commonMistakes?: CommonMistake[] | null;
  createdAt: string;
  // Variation support
  parentExerciseId?: number | null;
  parentExerciseName?: string | null;
  variationName?: string | null;
  hasVariations?: boolean;
  variationCount?: number;
  // Alternative (interchangeable/swappable) exercises support
  alternativeCount?: number;
  unilateral?: boolean;
  isometric?: boolean;
}

export interface ExerciseVariationsResponse {
  baseExercise: Exercise;
  variations: Exercise[];
  // Interchangeable exercises for the requested exercise (quick-swap in workouts)
  alternatives?: Exercise[];
}

export interface ExerciseAlternativesResponse {
  exercise: Exercise;
  alternatives: Exercise[];
}

export interface ExerciseHistoryEntry {
  id: number;
  name: string;
  muscleGroup: string;
  primaryMuscleGroup?: string | null;
  secondaryMuscleGroup?: string | null;
  tertiaryMuscleGroup?: string | null;
  parentExerciseId?: number | null;
  variationName?: string | null;
  lastPerformedAt?: string | null;
}

export interface CsvImportResult {
  message: string;
  created: number;
  updated: number;
  skipped: number;
  errors: string[];
}

export interface CreateVariationInput {
  variationName: string;
  name?: string;
  description?: string;
  instructions?: string;
  difficulty?: string;
  anatomyImages?: string[];
  instructionImages?: string[];
  youtubeVideoUrl?: string;
  cautionNotes?: string;
}

export interface CardioExercise {
  id: number;
  name: string;
  type: string; // 'running', 'cycling', 'rowing', 'jump_rope', 'swimming', 'walking', 'elliptical', 'stair_climbing'
  image?: string | null;
  createdAt: string;
}

export interface CardioSession {
  id: number;
  sessionDate: string;
  durationMinutes?: number;
  heartRateAvg?: number;
  heartRatePeak?: number;
  distance?: string; // Decimal stored as string in backend
  distanceUnit?: string; // 'km', 'miles', 'floors', 'meters'
  avgSpeed?: string;
  caloriesBurned?: number;
  notes?: string;
  resultImage?: string;
  createdAt: string;
  user: string; // IRI reference
  cardioExercise: CardioExercise;
}

export interface TrainingProgram {
  id: number;
  name: string;
  description?: string;
  introduction?: string;
  durationWeeks: number;
  workoutsPerWeek: number;
  difficulty: string;
  goal: string;
  active: boolean;
  currentPeriodType?: string | null;
  periodizationEnabled?: boolean;
  createdAt: string;
  updatedAt: string;
  user: string; // IRI reference
  workoutSessions?: WorkoutSession[];
}

export interface WorkoutSession {
  id: number;
  name: string;
  notes?: string | null;
  workoutDate: string;
  durationMinutes?: number | null;
  status: 'planned' | 'in_progress' | 'completed' | 'skipped';
  createdAt: string;
  completedAt?: string;
  startedAt?: string;
  totalPausedSeconds?: number;
  user: string; // IRI reference
  trainingProgram?: string; // IRI reference
  programDayNumber?: number | null;
  gym?: string | null; // IRI reference
  trainingLabel?: TrainingLabel | null;
  adaptationStatus?: string | null;
  checkInData?: WorkoutCheckInPayload | null;
  adaptationSummary?: WorkoutAdaptationSummary | null;
  sets?: Set[];
}

export interface WorkoutTrainerReport {
  workoutSessionId: number;
  fullReport: string;
  recommendedAdaptations?: string[];
  generatedAt?: string | null;
  notificationId?: number | null;
}

export interface WorkoutCheckInPayload {
  availableTimeMinutes: number;
  fatigueLevel: number;
  hasPain: boolean;
  painZone?: string | null;
  sleepQuality: number;
  goalFocus: 'performance' | 'technique' | 'recovery' | 'maintenance';
  missingEquipmentIds?: number[];
}

export interface WorkoutAdaptationChange {
  type: string;
  exerciseId?: number;
  exerciseName?: string;
  replacementExerciseId?: number;
  replacementExerciseName?: string;
  reason?: string;
}

export interface WorkoutAdaptationSummary {
  snapshotVersion?: number;
  status: 'applied' | 'adaptation_not_applied';
  appliedChanges?: WorkoutAdaptationChange[];
  rejectedChanges?: WorkoutAdaptationChange[];
  error?: string;
}

export interface Set {
  id: number;
  reps: number;
  repsLeft?: number | null;
  repsRight?: number | null;
  weight?: string; // Decimal stored as string in backend
  durationSeconds?: number;
  distance?: number;
  distanceUnit?: string;
  notes?: string;
  restSeconds?: number;
  setOrder: number;
  assistance?: boolean;
  rpe?: number;
  parentSetId?: number;
  dropSetOrder?: number;
  isSubstitution?: boolean;
  createdAt: string;
  exercise: string; // IRI reference
  workoutSession: string; // IRI reference
}

export interface ProgramExercise {
  id: number;
  exercise: Exercise;
  dayOfWeek: number;
  orderInSession: number;
  targetSets?: number;
  targetReps?: number;
  targetWeight?: number;
  targetDuration?: number;
  targetDistance?: number;
  notes?: string;
  supersetGroup?: string | null;
  rotationIndexes?: number[] | null;
  isFocused?: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface ProgramExercisesByDay {
  [dayOfWeek: number]: ProgramExercise[];
}

export interface ProgramDay {
  id?: number;
  dayNumber: number;
  name: string;
  weekday?: number | null;
  isDefault?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface ProgramDayNames {
  [dayNumber: number]: string;
}

export interface ProgramDayWeekdays {
  [dayNumber: number]: number;
}

export interface ProgramPeriodBlock {
  id: number;
  programId: number;
  periodType: string;
  goalType?: string | null;
  status: 'planned' | 'active' | 'completed' | 'cancelled';
  startDate?: string | null;
  endDate?: string | null;
  targetWeeks?: number | null;
  notes?: string | null;
  createdAt?: string;
  updatedAt?: string | null;
}

export interface ProgramPeriodTransition {
  id: number;
  programId: number;
  fromPeriodType?: string | null;
  toPeriodType: string;
  reason: 'manual' | 'goal_change' | 'plateau' | 'fatigue_management' | 'scheduled';
  effectiveDate: string;
  changedBy: number;
  metadata?: Record<string, any> | null;
  createdAt: string;
}

export interface ProgramPeriodRecommendation {
  recommended: boolean;
  reason: string;
  suggestedPeriodType: string;
  action: 'enable' | 'create_block' | 'transition' | 'stay';
  elapsedWeeks?: number | null;
  targetWeeks?: number | null;
}

export interface ProgramPeriodizationOverview {
  programId: number;
  periodizationEnabled: boolean;
  currentPeriodType?: string | null;
  activeBlock?: ProgramPeriodBlock | null;
  timeline: ProgramPeriodBlock[];
  transitions: ProgramPeriodTransition[];
  recommendation: ProgramPeriodRecommendation;
}

export interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
  createdAt: string;
  updatedAt: string;
}

export interface CoachProfile {
  name: string;
  tagline: string;
  avatarUrl?: string;
}

export interface FatigueNotificationData {
  score: number;
  level: 'low' | 'medium' | 'high' | 'unknown';
  windowStart: string;
  windowEnd: string;
  factors: string[];
  generatedAt: string;
  model?: string | null;
}

export interface CoachNotification {
  id: number;
  title: string;
  message: string;
  type: string;
  isRead: boolean;
  source?: string | null;
  metadata?: Record<string, any> | null;
  fatigue?: FatigueNotificationData | null;
  agentType?: string;
  agentAvatarKey?: string | null;
  createdAt: string;
  readAt?: string | null;
}

export type TrainerRunState = 'scheduled' | 'due' | 'idle' | 'failed';

export interface TrainerRunExecutionInfo {
  result: 'success' | 'failed';
  executedAt: string | null;
  errorMessage: string | null;
}

export interface NextTrainerRunInfo {
  state: TrainerRunState;
  scheduled: boolean;
  nextRunAt: string | null;
  remainingSeconds: number | null;
  lastExecution: TrainerRunExecutionInfo | null;
}

// API Collection response type
export interface ApiCollection<T> {
  '@context': string;
  '@id': string;
  '@type': string;
  totalItems: number;
  member: T[];
}

export interface ApiErrorBody {
  error?: string;
  message?: string;
  errors?: Record<string, string[]>;
}

// Input types for creating/updating resources
export interface CreateExerciseInput {
  name: string;
  description?: string;
  category: string;
  stimulusToFatigueRatio: string;
  equipment?: string;
  equipmentEntity?: string | null; // IRI reference to Equipment (e.g., "/api/equipment/1")
  muscleGroup: string;
  primaryMuscleGroup?: string;
  secondaryMuscleGroup?: string;
  tertiaryMuscleGroup?: string;
  instructions?: string;
  difficulty: string;
  anatomyImages?: string[];
  instructionImages?: string[];
  youtubeVideoUrl?: string;
  cautionNotes?: string;
  commonMistakes?: CommonMistake[];
  unilateral?: boolean;
  isometric?: boolean;
}

export interface CreateTrainingProgramInput {
  name: string;
  description?: string;
  durationWeeks: number;
  workoutsPerWeek: number;
  difficulty: string;
  goal: string;
  active?: boolean;
  user?: string; // IRI reference; omitted when derived from authenticated user
}

export interface CreateWorkoutSessionInput {
  name: string;
  notes?: string | null;
  workoutDate: string;
  durationMinutes?: number | null;
  status?: 'planned' | 'in_progress' | 'completed' | 'skipped';
  startedAt?: string;
  totalPausedSeconds?: number;
  completedAt?: string;
  user?: string; // IRI reference; omitted when derived from authenticated user
  trainingProgram?: string; // IRI reference
  programDayNumber?: number;
  gym?: string | null; // IRI reference
  trainingLabel?: TrainingLabel | null;
}

export interface CreateGymInput {
  name: string;
  type?: string;
  address?: string;
  equipment?: string[];
}

export interface UpdateGymInput extends Partial<CreateGymInput> {
  id: number;
}

export interface CreateSetInput {
  reps: number;
  repsLeft?: number;
  repsRight?: number;
  weight?: string; // Decimal stored as string in backend
  durationSeconds?: number;
  distance?: number;
  distanceUnit?: string;
  notes?: string;
  restSeconds?: number;
  setOrder: number;
  assistance?: boolean;
  rpe?: number;
  parentSetId?: number;
  dropSetOrder?: number;
  isSubstitution?: boolean;
  exercise: string; // IRI reference
  workoutSession: string; // IRI reference
}

export interface CreateCardioSessionInput {
  sessionDate: string;
  durationMinutes?: number;
  heartRateAvg?: number;
  heartRatePeak?: number;
  distance?: string;
  distanceUnit?: string;
  avgSpeed?: string;
  caloriesBurned?: number;
  notes?: string;
  resultImage?: string;
  user?: string; // IRI reference (optional – backend auto-assigns authenticated user)
  cardioExercise: string; // IRI reference
}

// Update types (all fields optional except id)
export interface UpdateExerciseInput extends Partial<CreateExerciseInput> {
  id: number;
}

export interface UpdateTrainingProgramInput extends Partial<CreateTrainingProgramInput> {
  id: number;
}

export interface UpdateWorkoutSessionInput extends Partial<CreateWorkoutSessionInput> {
  id: number;
}

export interface UpdateSetInput extends Partial<CreateSetInput> {
  id: number;
}

export interface CreateProgramExerciseInput {
  exerciseId: number;
  dayOfWeek: number;
  targetSets?: number;
  targetReps?: number;
  targetWeight?: number;
  targetDuration?: number;
  targetDistance?: number;
  notes?: string;
  supersetGroup?: string | null;
  rotationIndexes?: number[] | null;
  isFocused?: boolean | null;
}

export interface UpdateProgramExerciseInput {
  targetSets?: number;
  targetReps?: number;
  targetWeight?: number;
  targetDuration?: number;
  targetDistance?: number;
  notes?: string;
  dayOfWeek?: number;
  orderInSession?: number;
  supersetGroup?: string | null;
  rotationIndexes?: number[] | null;
  isFocused?: boolean | null;
}

export interface ProgramCatalogExerciseTemplate {
  title: string;
  muscleGroup?: string;
  category?: string;
  targetSets?: number;
  targetReps?: number;
  targetWeight?: number;
  notes?: string;
}

export interface ProgramCatalogDayTemplate {
  dayNumber: number;
  name: string;
  exercises: ProgramCatalogExerciseTemplate[];
}

export interface ProgramCatalogProgram {
  slug: string;
  name: string;
  description: string;
  goal: string;
  difficulty: string;
  durationWeeks: number;
  workoutsPerWeek: number;
  tags: string[];
  guru: {
    name: string;
    bio: string;
  };
  dayCount: number;
  exerciseCount: number;
  days: ProgramCatalogDayTemplate[];
}

export interface ProgramCatalogResponse {
  programs: ProgramCatalogProgram[];
}

export interface ProgramCatalogImportResponse {
  message: string;
  programId: number;
  programName: string;
  addedExercises: number;
  skippedExercises: Array<{
    dayNumber: number;
    title: string;
  }>;
}

export interface ReplaceProgramExerciseInput {
  newExerciseId: number;
}

// Progress tracking types
export interface WorkoutProgressExercise {
  exerciseId: number;
  exerciseName: string;
  stimulusToFatigueRatio?: string;
  currentE1rm?: number | null;
  currentPr?: number | null;
  sets: Array<{
    reps: number;
    repsLeft?: number | null;
    repsRight?: number | null;
    weight: number;
    rpe: number | null;
    assistance: boolean | null;
  }>;
}

export interface WorkoutProgressSession {
  id: number;
  name: string;
  notes?: string | null;
  date: string;
  programDayNumber: number | null;
  trainingLabel?: TrainingLabel | null;
  averageStimulusToFatigueRatio?: string | null;
  gymId?: number | null;
  gymName?: string | null;
  adaptationStatus?: string | null;
  checkInData?: WorkoutCheckInPayload | null;
  adaptationSummary?: WorkoutAdaptationSummary | null;
  exercises: WorkoutProgressExercise[];
}

export interface WorkoutProgressResponse {
  workouts: WorkoutProgressSession[];
}

export interface ExerciseProgressSession {
  sessionId: number;
  date: string;
  workoutName: string;
  notes?: string | null;
  programDayNumber: number | null;
  trainingLabel?: TrainingLabel | null;
  averageStimulusToFatigueRatio?: string | null;
  currentE1rm?: number | null;
  currentPr?: number | null;
  gymId?: number | null;
  gymName?: string | null;
  sets: Array<{
    reps: number;
    repsLeft?: number | null;
    repsRight?: number | null;
    weight: number;
    rpe: number | null;
    assistance: boolean | null;
  }>;
}

export interface ExerciseProgressResponse {
  workouts: ExerciseProgressSession[];
}

export interface ExerciseStatsRow {
  exerciseId: number;
  exerciseName: string;
  variationName: string | null;
  parentExerciseId: number | null;
  sessionCount: number;
  totalSets: number;
  totalReps: number;
  totalVolumeKg: number;
  avgWeightKg: number;
  maxWeightKg: number;
  avgRepsPerSet: number;
  avgRpe: number;
  bestEstimated1RMKg: number;
  lastSessionDate: string | null;
}

export interface ExerciseStatsResponse {
  stats: ExerciseStatsRow[];
}

export interface ExerciseRecordEntry {
  value: number;
  source: 'derived' | 'manual';
  sourceSetId: number | null;
  recordedAt: string | null;
  createdAt: string | null;
  note?: string | null;
}

export interface ExerciseRecordHistoryEntry extends ExerciseRecordEntry {
  id: number;
}

export interface ExerciseRecordsResponse {
  current: {
    e1rm: ExerciseRecordEntry | null;
    pr: ExerciseRecordEntry | null;
  };
  history: {
    e1rm: ExerciseRecordHistoryEntry[];
    pr: ExerciseRecordHistoryEntry[];
  };
}

export interface UpdateExercisePrInput {
  value: number;
  recorded_at?: string;
  note?: string;
}

// Session log data types for copy-to-clipboard feature
export interface SessionLogSet {
  reps: number;
  weight: number;
  assistance: boolean;
  rpe?: number | null;
  notes?: string | null;
}

export interface SessionLogExercise {
  exerciseId?: number;
  exerciseName: string;
  sets: SessionLogSet[];
}

export interface SessionPlannedLogSet {
  setOrder: number | null;
  targetReps: number | null;
  targetWeight: number | null;
  targetRpe: number | null;
  notes?: string | null;
}

export interface SessionPlannedLogExercise {
  exerciseId?: number;
  exerciseName: string;
  sets: SessionPlannedLogSet[];
}

export interface SessionLogData {
  id: number;
  name: string;
  notes?: string | null;
  date: string;
  focusedExerciseNames?: string[];
  durationMinutes?: number | null;
  gymName?: string | null;
  preWorkoutSummary: string | null;
  adaptationStatus?: string | null;
  checkInData?: WorkoutCheckInPayload | null;
  adaptationSummary?: WorkoutAdaptationSummary | null;
  plannedExercises: SessionPlannedLogExercise[];
  exercises: SessionLogExercise[];
}

export interface FocusedProgressPeriodSummary {
  deltaAvgWeightKg: number | null;
  deltaAvgRepsPerSet: number | null;
  deltaAvgRpe: number | null;
  currentWindowSessionCount: number;
  previousWindowSessionCount: number;
  insufficientData: boolean;
  status: 'ready' | 'insufficient_data';
}

export interface FocusedProgressItem {
  programExerciseId: number;
  programId: number;
  programName: string;
  dayOfWeek: number;
  orderInSession: number;
  exerciseId: number;
  exerciseName: string;
  summary: {
    '30d': FocusedProgressPeriodSummary;
    '90d': FocusedProgressPeriodSummary;
    '180d': FocusedProgressPeriodSummary;
  };
}

export interface FocusedProgressSummaryResponse {
  items: FocusedProgressItem[];
}

// Session edit data types for editing completed workouts
export interface SessionEditSet {
  id?: number;
  reps: number;
  weight: number;
  assistance: boolean;
  rpe?: number | null;
  notes?: string | null;
  setOrder: number;
}

export interface SessionEditExercise {
  exerciseId: number;
  exerciseName: string;
  sets: SessionEditSet[];
}

export interface SessionEditData {
  id: number;
  name: string;
  date: string;
  exercises: SessionEditExercise[];
}

export interface SessionEditPayload {
  exercises: {
    exerciseId: number;
    sets: {
      id?: number;
      reps: number;
      weight: number;
      assistance?: boolean;
      rpe?: number | null;
      notes?: string | null;
      setOrder?: number;
    }[];
  }[];
}

// Body Measurement types
export interface BodyMeasurement {
  id: number;
  metricType: string;
  value: string;
  unit?: string;
  measuredAt: string;
  notes?: string;
  createdAt: string;
  user: string;
}

export interface BodyMeasurementsByDate {
  date: string;
  measurements: { [metricType: string]: number };
}

export interface BodyMeasurementChartPoint {
  date: string | null;
  measured_at?: string | null;
  value: number;
}

export interface BatchMeasurementInput {
  measuredAt: string;
  measurements: { [metricType: string]: number };
  userId?: number;
}

export interface LatestMeasurements {
  [metricType: string]: { value: number; date: string };
}

export interface ProgressEstimation {
  id: number;
  user_id: number;
  metric_type: 'weight' | 'reps' | 'body_fat';
  current_value: number;
  predicted_value: number;
  target_value: number | null;
  target_date: string;
  model_version: string;
  status: 'on-track' | 'off-track' | 'unknown';
  difference_to_target: number | null;
  actual_value: number | null;
  error_mae: number | null;
  error_mape: number | null;
  created_by_trainer_id: number | null;
  created_at: string;
  updated_at: string | null;
}

export interface ProgressEstimationsResponse {
  estimations: ProgressEstimation[];
}

export interface ProgressEstimationManagedUser {
  id: number;
  display_name: string;
  email: string;
}

export interface ProgressEstimationManagedUsersResponse {
  managed_users: ProgressEstimationManagedUser[];
}

export interface ProgressEstimationAccuracyAggregate {
  samples: number;
  avg_mae: number | null;
  avg_mape: number | null;
}

export interface ProgressEstimationAccuracyTimelinePoint {
  date: string;
  avg_mae: number;
  avg_mape: number;
  samples: number;
}

export interface ProgressEstimationAccuracyResponse {
  window_days: number;
  aggregate: ProgressEstimationAccuracyAggregate;
  timeline: ProgressEstimationAccuracyTimelinePoint[];
}

export interface CreateProgressEstimationInput {
  user_id?: number;
  metric_type: 'weight' | 'reps' | 'body_fat';
  target_date: string;
  target_value?: number;
  model_version?: string;
}

// Workout Planning types
export interface PlanTemplateSuggestedSet {
  setOrder: number;
  targetReps: number;
  targetWeight: number | null;
  targetRpe: number | null;
  source: 'last_workout' | 'program' | 'calendar';
}

export interface PlanTemplateExercise {
  exerciseId: number;
  exerciseName: string;
  muscleGroup: string;
  stimulusToFatigueRatio: string;
  hasVariations: boolean;
  parentExerciseId: number | null;
  variationName: string | null;
  programNotes: string | null;
  suggestedSets: PlanTemplateSuggestedSet[];
}

export interface PlanTemplateRotation {
  rotationName: string;
  currentIndex: number;
  currentLabel: string;
  rotationLabels: string[];
  rotationCount: number;
  recommendedIndex?: number;
  recommendedLabel?: string | null;
  selectionMode?: 'neutral' | 'sfr_balanced';
  confidence?: 'low' | 'medium' | 'high';
  historyWindow?: number;
  historySampleSize?: number;
  historicalAverageSfr?: string | null;
}

export interface PlanTemplate {
  programId: number;
  programName: string;
  dayNumber: number;
  exercises: PlanTemplateExercise[];
  rotation?: PlanTemplateRotation;
}

export interface CalendarPlanSource {
  id: number;
  name: string;
  workoutDate: string;
  status: string;
  programId: number | null;
  programName: string | null;
  dayNumber: number | null;
  exerciseCount: number;
  setCount: number;
}

export interface CalendarPlanSourcesResponse {
  sources: CalendarPlanSource[];
}

export interface PlanFromSessionTemplate extends PlanTemplate {
  sourceSession: CalendarPlanSource;
}

export interface PlannedSetInput {
  setOrder: number;
  targetReps: number;
  targetWeight: number | null;
  targetRpe: number | null;
  notes?: string;
}

export interface CreateWorkoutPlanExercise {
  exerciseId: number;
  isSubstitution?: boolean;
  sets: PlannedSetInput[];
}

export interface CreateWorkoutPlanInput {
  name: string;
  workoutDate: string;
  programId?: number;
  dayNumber?: number;
  notes?: string;
  exercises: CreateWorkoutPlanExercise[];
}

export interface PlannedSetDetail {
  id: number;
  setOrder: number;
  targetReps: number;
  targetWeight: number | null;
  targetRpe: number | null;
  notes: string | null;
}

export interface WorkoutPlanExerciseDetail {
  exerciseId: number;
  exerciseName: string;
  muscleGroup: string;
  isSubstitution: boolean | null;
  sets: PlannedSetDetail[];
}

export interface WorkoutPlanDetail {
  id: number;
  name: string;
  status: string;
  workoutDate: string;
  programId: number | null;
  programName: string | null;
  dayNumber: number | null;
  adaptationStatus?: string | null;
  checkInData?: WorkoutCheckInPayload | null;
  adaptationSummary?: WorkoutAdaptationSummary | null;
  exercises: WorkoutPlanExerciseDetail[];
}

export interface PendingPlanProgram {
  programId: number;
  programName: string;
  totalDays: number[];
  plannedDays: number[];
  inProgressDays: number[];
  completedDays: number[];
  unplannedDays: number[];
}

export interface PendingPlanStatus {
  needsPlanning: boolean;
  programs: PendingPlanProgram[];
}

export interface SetSuccessDetail {
  setOrder: number;
  plannedReps: number;
  plannedWeight: number | null;
  actualReps: number | null;
  actualWeight: number | null;
  rate: number;
}

export interface ExerciseSuccessDetail {
  exerciseId: number;
  exerciseName: string;
  rate: number;
  sets: SetSuccessDetail[];
}

export interface SessionSuccessRate {
  hasPlannedData: boolean;
  sessionId?: number;
  successRate: number | null;
  exercises?: ExerciseSuccessDetail[];
}

export interface BatchSuccessRateEntry {
  sessionId: number;
  date: string;
  name: string;
  dayNumber: number | null;
  successRate: number;
}

export interface BatchSuccessRates {
  rates: BatchSuccessRateEntry[];
}

export interface AdaptationValueSnapshot {
  targetSets: number;
  targetReps: number;
  targetWeight: number | null;
  targetRpe: number | null;
}

export interface AdaptationRecommendation {
  id: number;
  programId: number;
  programExerciseId: number;
  exerciseId: number;
  exerciseName: string;
  dayNumber: number;
  status: 'pending' | 'accepted' | 'rejected';
  isActive: boolean;
  appliesToFuturePlanning: boolean;
  current: AdaptationValueSnapshot;
  proposed: AdaptationValueSnapshot;
  rejectionReason: string | null;
  createdAt: string;
  updatedAt: string | null;
  createdBy: number | null;
  updatedBy: number | null;
}

export interface AdaptationRecommendationsResponse {
  programId: number;
  recommendations: AdaptationRecommendation[];
}

export interface AdaptationRecommendationsHistoryResponse {
  programId: number;
  history: AdaptationRecommendation[];
}

export interface AdaptationReviewResponse {
  programId: number;
  dayNumber: number;
  generated: number;
  correlationId: string;
}

export interface FindPlanForDayResponse {
  found: boolean;
  session: {
    id: number;
    name: string;
    workoutDate: string;
    dayNumber: number;
  } | null;
}

// Calendar types
export interface CalendarDayMapping {
  dayNumber: number;
  dayName: string;
  weekday: number | null;
}

export interface CalendarProgram {
  id: number;
  name: string;
  dayMappings: CalendarDayMapping[];
}

export interface CalendarWorkoutSession {
  id: number;
  date: string;
  name: string;
  notes?: string | null;
  status: string;
  programDayNumber: number | null;
  programId: number | null;
  exerciseCount: number;
  setCount: number;
  durationMinutes: number | null;
  trainingLabel?: TrainingLabel | null;
  adaptationStatus?: string | null;
  checkInData?: WorkoutCheckInPayload | null;
  adaptationSummary?: WorkoutAdaptationSummary | null;
}

export interface WorkoutAdaptationContext {
  skipEquipmentQuestion: boolean;
  sessionGymId: number | null;
  defaultGymId: number | null;
  equipmentOptions: Array<{ id: number; name: string }>;
}

export interface WorkoutAdaptationResponse {
  status: 'applied' | 'adaptation_not_applied';
  adaptationApplied: boolean;
  snapshotVersion?: number;
  message?: string;
  plan: {
    sessionId: number;
    sessionName: string;
    dayNumber: number | null;
    exercises: Array<{
      exerciseId: number;
      exerciseName: string;
      muscleGroup: string;
      category: string;
      equipmentId: number | null;
      equipmentName: string | null;
      isSubstitution: boolean;
      originalExerciseId?: number;
      sets: Array<{
        setOrder: number;
        targetReps: number;
        targetWeight: number | null;
        targetRpe: number | null;
      }>;
    }>;
  };
  appliedChanges: WorkoutAdaptationChange[];
  rejectedChanges: WorkoutAdaptationChange[];
}

export interface CalendarCardioSession {
  id: number;
  date: string;
  cardioExerciseId?: number;
  exerciseName: string;
  durationMinutes: number | null;
  distance: string | null;
  distanceUnit: string | null;
  heartRateAvg: number | null;
  heartRatePeak?: number | null;
  avgSpeed?: string | null;
  caloriesBurned?: number | null;
  resultImage?: string | null;
}

export interface CalendarData {
  dateRange: { from: string; to: string };
  activeProgram: CalendarProgram | null;
  workoutSessions: CalendarWorkoutSession[];
  cardioSessions: CalendarCardioSession[];
}

// Rotational Training types
export interface RotationalConfig {
  id: number;
  programId: number;
  dayNumber: number;
  rotationName: string;
  rotationLabels: string[];
  currentIndex: number;
  currentLabel: string;
  rotationCount: number;
  createdAt: string;
  updatedAt: string | null;
}

export interface RotationalConfigResponse {
  found: boolean;
  config: RotationalConfig | null;
}

export interface RotationalConfigAllResponse {
  programId: number;
  rotations: RotationalConfig[];
}

export interface SaveRotationalConfigInput {
  rotationName: string;
  rotationLabels: string[];
}

export interface AdvanceRotationResponse {
  message: string;
  previousIndex: number;
  previousLabel: string;
  currentIndex: number;
  currentLabel: string;
}

// Coach Profile Settings types
export interface CoachProfilePolicies {
  allow_calorie_targets: boolean;
  allow_macro_targets: boolean;
  allow_cardio_frequency_changes: boolean;
  allow_same_day_cardio_and_lifting: boolean;
  allow_double_sessions: boolean;
  allow_aggressive_weight_loss_advice: boolean;
  allow_aggressive_weight_gain_advice: boolean;
  allow_exercise_swaps: boolean;
  allow_volume_reduction: boolean;
  allow_deload_suggestions: boolean;
}

export interface CoachProfileSettingsData {
  id: number;
  presetName: string | null;
  capabilities: string[];
  primaryGoal: string;
  secondaryGoal: string | null;
  phase: string;
  trainingPriority: string;
  experienceLevel: string;
  recoveryCapacity: string;
  policies: CoachProfilePolicies;
  adviceScope: string;
  customNotes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CoachProfileDefaults {
  capabilities: string[];
  primaryGoals: string[];
  phases: string[];
  trainingPriorities: string[];
  experienceLevels: string[];
  recoveryCapacities: string[];
  adviceScopes: string[];
}

export interface CoachProfileSettingsResponse {
  profile: CoachProfileSettingsData | null;
  defaults: CoachProfileDefaults;
}

export interface CoachProfilePresetDefinition {
  label: string;
  description: string;
  capabilities: string[];
  goalProfile: {
    primaryGoal: string;
    secondaryGoal: string | null;
    phase: string;
    trainingPriority: string;
    experienceLevel: string;
    recoveryCapacity: string;
  };
  policies: CoachProfilePolicies;
  adviceScope: string;
}

export type CoachProfilePresetsResponse = Record<string, CoachProfilePresetDefinition>;

export interface CoachProfileApplyPresetResponse {
  profile: CoachProfileSettingsData;
  context: Record<string, unknown>;
}

export const METRIC_TYPES = [
  { key: 'weight', label: 'Тегло', unit: 'kg' },
  { key: 'neck', label: 'Размер Врат', unit: 'cm' },
  { key: 'waist', label: 'Размер Талия', unit: 'cm' },
  { key: 'chest', label: 'Размер Гръдна обиколка', unit: 'cm' },
  { key: 'bicep', label: 'Бицепс обиколка', unit: 'cm' },
  { key: 'shoulder', label: 'Рамена обиколка', unit: 'cm' },
  { key: 'leg', label: 'Крак обиколка', unit: 'cm' },
];

// ── Other Sports ─────────────────────────────────────────────────────────────

export interface OtherSport {
  id: number;
  name: string;
  emoji: string | null;
  isSystemSport: boolean;
  createdAt: string;
}

export interface OtherSportSession {
  id: number;
  sport: OtherSport;
  sessionDate: string;
  durationMinutes: number | null;
  notes: string | null;
  createdAt: string;
}

export interface CreateOtherSportSessionData {
  sport: string; // IRI, e.g. /api/other_sports/1
  sessionDate: string; // ISO 8601 date string
  durationMinutes?: number | null;
  notes?: string | null;
}

export interface UpdateOtherSportSessionData {
  sessionDate?: string;
  durationMinutes?: number | null;
  notes?: string | null;
}
